import { useMutation, useQueryClient } from 'react-query';
import { createRole, deleteRole, refreshGrantSeeds, refreshPlaceSeeds } from './acl';
import {
  approveTargetSubmission,
  confirmAllEntries,
  rejectTargetSubmission,
  submitChanges,
  submitSources,
  submitTargets,
} from './measurement';
import { addUser, addUserRoles, removeUserRole, userUpdateStatus, saveUserSettings  } from './user';
import { createExcelFile } from './excel';
import { CategoriesEnum } from '../common/enum';

const useAddUser = () => {
  const queryClient = useQueryClient();

  return useMutation(addUser, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userList'] });
    },
  });
};

const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation(createRole, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roleList'] });
    },
  });
};

const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteRole, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roleList'] });
    },
  });
};

const useAddUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation(addUserRoles, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userDetail'] });
    },
  });
};

const useRemoveUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation(removeUserRole, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userDetail'] });
    },
  });
};

// Custom hook to update user status.
const useUserUpdateStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(userUpdateStatus, {
    onSuccess: () => {
      // Invalidate the 'userDetail' query to refetch the data and keep the cache in sync
      queryClient.invalidateQueries({ queryKey: ['userDetail'] });
    },
  });
};

const useSubmitChanges = () => {
  const queryClient = useQueryClient();

  return useMutation(submitChanges, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['millInit'] });
      queryClient.invalidateQueries({ queryKey: ['consumptionView'] });
    },
  });
};

const useSubmitTargetChanges = () => {
  const queryClient = useQueryClient();

  return useMutation(submitTargets, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['targetView'] });
      queryClient.invalidateQueries({ queryKey: ['submittedTargets'] });
    },
  });
};

const useConfirmAllEntries = () => useMutation(confirmAllEntries);

const useApproveTargetSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation(approveTargetSubmission, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['targetView'] });
      queryClient.invalidateQueries({ queryKey: ['submittedTargets'] });
    },
  });
};

const useRejectTargetSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation(rejectTargetSubmission, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submittedTargets'] });
    },
  });
};

const useSubmitSources = () => {
  const queryClient = useQueryClient();

  return useMutation(submitSources, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sourcesView'] });
    },
  });
};

const useRefreshPlaceSeeds = () => {
  const queryClient = useQueryClient();

  return useMutation(refreshPlaceSeeds, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['placeList'] });
    },
  });
};

const useRefreshGrantSeeds = () => {
  const queryClient = useQueryClient();

  return useMutation(refreshGrantSeeds, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grantList'] });
    },
  });
};

const useCreateExcelFile = () => {
  return useMutation(
    async ({ placeCode, tabName, sourceName, selectedTime }: { placeCode: string; tabName: string; sourceName: string, selectedTime: number }) => {
      return createExcelFile(placeCode, tabName, sourceName, selectedTime);
    },
    {
      onError: (error: any) => {
        // Re-throw the error to pass it through
        throw error;
      },
    },
  );
};


/**
 * Used to persist hidden column settings.
 * @param settingKey - The key identifying which user setting to save
 */
const useSaveUserSettings = (settingKey: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ category, hidden }: { category: CategoriesEnum; hidden: string[] }) => {

      const currentData =
        queryClient.getQueryData<{ settingsJson: string }>(['userSettings', settingKey]) ||  { settingsJson: '{}' };

      let hiddenColumnsByCategory: Record<string, string[]> = {};
      try {
        hiddenColumnsByCategory = JSON.parse(currentData.settingsJson);
      } catch (error) {
        hiddenColumnsByCategory = {};
      }

      // Merge/update the hidden columns for the current category.
      hiddenColumnsByCategory[category] = hidden;

      // Construct the payload with the dynamic settingKey.
      const payload = {
        SettingsKey: settingKey,
        SettingsJson: JSON.stringify(hiddenColumnsByCategory),
      };

      return saveUserSettings(payload);
    },
    {
      onSuccess: () => {
        // Invalidate only this key's query, forcing an immediate refetch of updated data.
        queryClient.invalidateQueries(['userSettings', settingKey]);
      },
      onError: (error: any) => {
        throw error;
      },
    }
  );
};

export {
  useAddUser,
  useCreateRole,
  useDeleteRole,
  useAddUserRole,
  useRemoveUserRole,
  useUserUpdateStatus,
  useSubmitChanges,
  useSubmitTargetChanges,
  useConfirmAllEntries,
  useApproveTargetSubmission,
  useRejectTargetSubmission,
  useSubmitSources,
  useRefreshPlaceSeeds,
  useRefreshGrantSeeds,
  useCreateExcelFile,
  useSaveUserSettings
};
